/*!
 * patterns.js — the pattern generators (Pseq, Prand, Pxrand, Pshuf, Pwhite
 * ...) and evalMaybeRandom (rand(), choose() ...), on their own
 * © 2026 Rob Canning — GPLv3
 *
 * Pure: no audio, no page. audioShared.js re-exports them, so every import
 * from there still works; what only needs patterns (patternSteps.js, a
 * test in node) imports them from here, without the audio context that
 * audioShared makes as it loads.
 */

import { compilePipe, pipeValue, evenPick } from "./pitchSieve.js";

export function evalMaybeRandom(v) {
  if (v == null) return v;

  // Handle { type: "rand", min, max } from extractValueExpr
  if (typeof v === "object" && (v.type === "rand" || v.type === "irand" || v.type === "birand")) {
    const result = v.min + Math.random() * (v.max - v.min);
    if (v.type === "birand") {
      // birand: 50/50 chance of positive or negative range
      return Math.random() < 0.5 ? -result : result;
    }
    return v.type === "irand" ? Math.floor(result) : result;
  }

  // Handle { type: "funcCall", name: "rand", args: [min, max] }
  if (typeof v === "object" && v.type === "funcCall") {
    // rand([a, b, c]) — pick one of a list, the runtime twin of propagate's
    // compile-time rnd([...]); choose(a, b, c) -- the same pick, without the brackets
    const listed = v.name === "choose" ? (v.args?.length === 1 && Array.isArray(v.args[0]) ? v.args[0] : v.args)
      : (v.name === "rand" || v.name === "rnd") && v.args?.length === 1 && Array.isArray(v.args[0]) ? v.args[0]
      : null;
    if (listed) {
      const list = listed;
      if (!list.length) return null;
      const pick = list[Math.floor(Math.random() * list.length)];
      const n = Number(pick);
      return Number.isNaN(n) ? pick : n;
    }
    if ((v.name === "rand" || v.name === "irand" || v.name === "birand" || v.name === "hrand") && v.args?.length === 2) {
      const a = Number(v.args[0]);
      const b = Number(v.args[1]);
      if (!isNaN(a) && !isNaN(b)) {
        const result = a + Math.random() * (b - a);
        if (v.name === "birand") {
          // birand: 50/50 chance of positive or negative range
          return Math.random() < 0.5 ? -result : result;
        }
        return v.name === "irand" ? Math.floor(result) : result;
      }
    }
    return null;
  }

  // Handle plain string like rand(0.5,2) or birand(0.3) — produced when DSL was
  // saved with quotes around the expression (e.g. speed:"rand(0.5,2)")
  if (typeof v === "string") {
    const m = v.replace(/['"]/g, "").trim()
               .match(/^(rand|irand|birand)\(\s*([-\d.]+)(?:\s*,\s*([-\d.]+))?\s*\)$/);
    if (m) {
      const name = m[1];
      const a = Number(m[2]);
      const b = m[3] !== undefined ? Number(m[3]) : a;
      if (!isNaN(a) && !isNaN(b)) {
        if (name === "birand") {
          const mag = a + Math.random() * (b - a);
          return Math.random() < 0.5 ? -mag : mag;
        }
        const result = a + Math.random() * (b - a);
        return name === "irand" ? Math.floor(result) : result;
      }
    }
  }

  return v;
}

// =============================================================
//  Shared pattern generator (Pseq/Prand/Pxrand/Pshuf) with nested
//  pattern support and hrand() (held random). Used for audio speed
//  patterns (audioFile/audioPool) and audioImpulse selection/speed.
// =============================================================

/** A copy of arr in a random order, every order as likely (Fisher-Yates). */
export function shuffle(arr) {
  const a = arr.slice();
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

/**
 * The generator for an animation's values: Prand([a, b, c], inf) -- the list
 * in brackets, as in SuperCollider -- steps through a, b, c. To synth()
 * brackets in a pattern are a chord; a size, an angle or a colour has none,
 * so for those the brackets are the list.
 */
export function makeValueGenerator(pattern) {
  const v = pattern?.values;
  const listed = v?.length === 1 && Array.isArray(v[0]) ? { ...pattern, values: v[0] } : pattern;
  return makePatternGenerator(listed);
}

// name -> [how many arguments, their defaults], as SuperCollider has them
const DIST_ARGS = {
  Pbrown: [3, [0, 1, 0.125]],
  Pgauss: [2, [0, 1]],
  Plprand: [2, [0, 1]],
  Phprand: [2, [0, 1]],
  Pmeanrand: [2, [0, 1]],
  Pbeta: [4, [0, 1, 1, 1]],
  Pcauchy: [2, [0, 1]],
  Ppoisson: [1, [1]],
  Pbjorklund: [2, [3, 8]],
  Peuclid: [2, [3, 8]],
};

/** One value of a chance pattern: its args, the step it is on; a walk keeps its place on `d`. */
export function drawDist(name, d, step = 0) {
  const r = Math.random;
  const [a, b, c, e] = d.args;
  switch (name) {
    case "Pbrown": {
      // a random walk: from somewhere in the range, a step of up to `c` either way, folded back in at the ends
      const lo = Math.min(a, b), hi = Math.max(a, b);
      let x = d.walk ?? lo + r() * (hi - lo);
      x += (r() * 2 - 1) * Math.abs(c);
      const span = hi - lo;
      if (span > 0) {
        let t = (x - lo) % (2 * span);
        if (t < 0) t += 2 * span;
        x = lo + (t > span ? 2 * span - t : t);
      } else x = lo;
      d.walk = x;
      return x;
    }
    case "Pgauss": {
      // the bell curve: mostly near the mean, a deviation either side
      const u = 1 - r(), v = r();
      return a + b * Math.sqrt(-2 * Math.log(u)) * Math.cos(2 * Math.PI * v);
    }
    case "Plprand": return a + Math.min(r(), r()) * (b - a);       // leaning low
    case "Phprand": return a + Math.max(r(), r()) * (b - a);       // leaning high
    case "Pmeanrand": return a + ((r() + r()) / 2) * (b - a);      // leaning to the middle
    case "Pbeta": {
      // Johnk's beta: prob1 and prob2 below 1 lean to the ends, above 1 to the middle
      const p1 = 1 / Math.max(1e-6, c), p2 = 1 / Math.max(1e-6, e);
      for (let i = 0; i < 1000; i++) {
        const x = Math.pow(r(), p1), y = Math.pow(r(), p2);
        if (x + y <= 1 && x + y > 0) return a + (x / (x + y)) * (b - a);
      }
      return a + r() * (b - a);
    }
    case "Pcauchy": return a + b * Math.tan(Math.PI * (r() - 0.5));   // like the bell curve, with far wilder outliers
    case "Ppoisson": {
      // how many, when `a` on average: 0, 1, 2 ... (Knuth)
      const L = Math.exp(-Math.max(0, a));
      let k = 0, p = 1;
      do { k++; p *= r(); } while (p > L && k < 10000);
      return k - 1;
    }
    case "Pbjorklund": {
      // Euclidean: `a` hits as evenly as can be over `b` steps, from a hit -- 1 a hit, 0 none
      const n = Math.max(1, Math.round(b)), k = Math.max(0, Math.min(n, Math.round(a)));
      return ((step % n) * k) % n < k ? 1 : 0;
    }
    case "Peuclid": {
      // peuclid(k, n, rot:2): Pbjorklund's hits, begun `rot` steps in
      const n = Math.max(1, Math.round(b)), k = Math.max(0, Math.min(n, Math.round(a)));
      const at = (((step + Math.round(d.rot || 0)) % n) + n) % n;
      return (at * k) % n < k ? 1 : 0;
    }
  }
  return null;
}

/**
 * every:6 -- the first step of each six goes through; every:peuclid(3, 8) --
 * the steps a pattern gives a 1 (any number above 0). Counted from the run of
 * the line, the same steps each time: chance: is the one that throws dice.
 * null for nothing to count by (every: not given, or unreadable).
 */
export function makeEvery(spec) {
  if (spec == null) return null;
  if (typeof spec === "object" && spec.type === "pattern") {
    let gen = makePatternGenerator(spec);
    return {
      on() {
        let v = gen.next();
        if (v == null) { gen = makePatternGenerator(spec); v = gen.next(); }   // used up: round again
        return Number(v) > 0;
      },
    };
  }
  const n = Math.round(Number(spec));
  if (!Number.isFinite(n) || n < 1) return null;
  let step = 0;
  return { on: () => step++ % n === 0 };
}

export function makePatternGenerator(pattern) {
  // .values is what the parser emits; .args is accepted too
  const rawValues = pattern?.values ?? pattern?.args;
  if (!rawValues?.length) return null;

  // hrand(a, b) resolves ONCE per generator (held across passes);
  // rand(a, b) stays a funcCall and re-evaluates per hit downstream.
  const resolveHeld = (val) => {
    if (Array.isArray(val)) return val.map(resolveHeld);
    if (val && typeof val === "object" && val.type === "funcCall" && val.name === "hrand") {
      const r = evalMaybeRandom({ ...val, name: "rand" });
      return (r != null && Number.isFinite(r)) ? r : val;
    }
    return val;
  };
  const values = rawValues.slice().map(resolveHeld);

  // Nested patterns: a step that is itself a pattern object steps its own
  // child generator (recreated when exhausted) — same semantics as the
  // synth pattern engine.
  const nestedGens = new Map();
  const resolveStep = (v) => {
    if (v && typeof v === "object" && v.type === "pattern") {
      if (!nestedGens.has(v)) nestedGens.set(v, makePatternGenerator(v));
      let nv = nestedGens.get(v)?.next();
      if (nv == null) {
        nestedGens.set(v, makePatternGenerator(v));
        nv = nestedGens.get(v)?.next();
      }
      return nv;
    }
    return v;
  };
  let repeats = pattern.repeats;
  if (repeats === "inf" || repeats === Infinity || repeats == null) repeats = Infinity;
  else {
    repeats = Number(repeats);
    if (!Number.isFinite(repeats)) repeats = 1;
  }

  let index = 0;
  let last = null;
  let cycleCount = 0;

  const name = String(pattern.name || "Pseq");

  // Pwhite / Pexprand: (lo, hi[, length]). The parser reads a trailing
  // number as the repeat count, so Pwhite(100, 1000) arrives as values [100]
  // and repeats 1000: that is the range's top, and it goes on for ever.
  const range = (() => {
    if (name !== "Pwhite" && name !== "Pexprand") return null;
    const nums = values.map(Number).filter(Number.isFinite);
    if (nums.length >= 2) return { lo: nums[0], hi: nums[1], length: repeats };
    if (nums.length === 1 && Number.isFinite(repeats)) return { lo: nums[0], hi: repeats, length: Infinity };
    return { lo: nums[0] ?? 0, hi: nums[0] ?? 1, length: repeats };
  })();

  // SuperCollider's chance (as it writes them; a last number past these the
  // length): Pbrown(lo, hi, step), Pgauss(mean, dev), Plprand / Phprand /
  // Pmeanrand(lo, hi), Pbeta(lo, hi, prob1, prob2), Pcauchy(mean, spread),
  // Ppoisson(mean), Pbjorklund(k, n). The parser took the last number for a
  // count: it is one of these again unless it was inf.
  const dist = (() => {
    if (!(name in DIST_ARGS)) return null;
    const nums = values.map(Number).filter(Number.isFinite);
    const all = Number.isFinite(repeats) ? [...nums, repeats] : nums;
    const [n, defaults] = DIST_ARGS[name];
    const args = defaults.map((d, i) => all[i] ?? d);
    return { args, length: all.length > n ? all[n] : Infinity, walk: null, rot: Number(pattern.rot) || 0 };
  })();
  // pwrand(a:3, b:1, c:1, inf) -- the parser makes it SuperCollider's two
  // lists, Pwrand([a, b, c], [3, 1, 1], inf): how likely each is (any numbers: summed to 1)
  const weighted = (() => {
    if (name !== "Pwrand") return null;
    const [items, weights] = values;
    if (!Array.isArray(items) || !Array.isArray(weights)) {
      console.warn("[pwrand] each value and its weight: pwrand(a:3, b:1, c:1, inf) -- each one as likely meanwhile");
      return { items: values, cum: values.map((_, i) => (i + 1) / values.length) };
    }
    const w = items.map((_, i) => Math.max(0, Number(weights[i]) || 0));
    const sum = w.reduce((a, b) => a + b, 0) || 1;
    let acc = 0;
    return { items, cum: w.map(x => (acc += x / sum)) };
  })();

  const gen = {
    values,
    name,
    lastValue: null,
    // Which slot the last next() came from. synth reads this per voice to
    // report which step of the pattern is sounding; harmless for everyone else.
    lastIndex: 0,

    next() {
      let v;

      if (name === "Pwhite" || name === "Pexprand") {
        // a range, as in SuperCollider: Pwhite(lo, hi, length) uniform,
        // Pexprand(lo, hi, length) even across octaves (for pitch)
        if (cycleCount >= range.length) return null;
        cycleCount += 1;
        gen.lastIndex = 0;
        const r = Math.random();
        v = name === "Pexprand" && range.lo > 0 && range.hi > 0
          ? range.lo * Math.pow(range.hi / range.lo, r)
          : range.lo + r * (range.hi - range.lo);
      } else if (dist) {
        if (cycleCount >= dist.length) return null;
        gen.lastIndex = name === "Pbjorklund" || name === "Peuclid" ? cycleCount % Math.max(1, Math.round(dist.args[1])) : 0;
        v = drawDist(name, dist, cycleCount);
        cycleCount += 1;
      } else if (weighted) {
        if (cycleCount >= repeats) return null;
        const r = Math.random();
        let idx = weighted.cum.findIndex(c => r < c);
        if (idx < 0) idx = weighted.items.length - 1;
        gen.lastIndex = idx;
        v = weighted.items[idx];
        cycleCount += 1;
      } else if (name === "Prand") {
        // as in SuperCollider: the trailing number counts values, not passes
        // of the list (Prand(a, b, c, 4) is four of them)
        if (cycleCount >= repeats) return null;
        const idx = Math.floor(Math.random() * values.length);
        gen.lastIndex = idx;
        v = values[idx];
        cycleCount += 1;
      } else if (name === "Pchoose") {
        // a random one each step; a trailing number counts picks:
        // Pchoose(a, b, c, 4) is four of them
        if (cycleCount >= repeats) return null;
        const idx = Math.floor(Math.random() * values.length);
        gen.lastIndex = idx;
        v = values[idx];
        cycleCount += 1;
      } else if (name === "Pxrand" || name === "Prandx") {
        if (cycleCount >= repeats) return null;
        let safety = 0, idx;
        do {
          idx = Math.floor(Math.random() * values.length);
          v = values[idx];
          safety++;
        } while (v === last && values.length > 1 && safety < 100);
        gen.lastIndex = idx;
        last = v;
        cycleCount += 1;                  // values, as Prand's
      } else if (name === "Pshuf") {
        if (cycleCount >= repeats) return null;
        if (!gen._shuffled) gen._shuffled = shuffle(values);
        gen.lastIndex = index;
        v = gen._shuffled[index];
        index++;
        if (index >= gen._shuffled.length) {
          index = 0;
          gen._shuffled = shuffle(values);
          cycleCount++;
        }
      } else {
        // Default: Pseq
        if (cycleCount >= repeats) return null;
        gen.lastIndex = index;
        v = values[index];
        index++;
        if (index >= values.length) {
          index = 0;
          cycleCount++;
        }
      }

      v = resolveStep(v);
      gen.lastValue = v;
      return v;
    },

    reset() {
      index = 0;
      last = null;
      cycleCount = 0;
      if (dist) dist.walk = null;          // Pbrown: from somewhere new
      if (name === "Pshuf") {
        gen._shuffled = shuffle(values);
      }
    }
  };

  // Prand(...)*0.7, Pseq(...)+=10: every value drawn, times / plus (in
  // order) -- numbers and chords; a note name or a rand() passes as it is
  const ops = Array.isArray(pattern.ops) ? pattern.ops.filter(o => o?.type === "rel") : [];
  if (ops.length) {
    const draw = gen.next.bind(gen);
    const apply = v => (typeof v === "number" && Number.isFinite(v)
      ? ops.reduce((x, o) => (o.op === "*" ? x * o.by : x + o.by), v)
      : Array.isArray(v) ? v.map(apply) : v);
    gen.next = () => apply(draw());
  }
  // Pwhite(40, 100) | scale(minor): each value drawn, through the pitch
  // filters (pitchSieve.js) -- Hz out; `even`: a pitch they let through
  // between Pwhite's ends, each as likely
  if (Array.isArray(pattern.pipe) && pattern.pipe.length) {
    const pipe = compilePipe(pattern.pipe);
    const draw = gen.next.bind(gen);
    gen.next = () => {
      const v = draw();
      if (v == null) return v;
      if (pipe.even && range) { const hz = evenPick(range.lo, range.hi, pipe); if (hz != null) return hz; }
      return pipeValue(v, pipe);
    };
  }
  return gen;
}
